import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, Box, ChevronRight, CheckSquare, Tag, Pencil, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CreateTodoModal } from '../components/todos';
import { Toast } from '../components/ui';
import { createTodo } from '../services/todoService';
import type { CreateTodoInput } from '../types/todo';
import {
  ProductGallery,
  ProductSections,
  ProductAttributes,
  ProductBundleItems,
  ProductDocuments,
  ProductRelations,
  ProductMarketingMaterials,
} from '../components/products';
import { getProductBySlug, formatPrice, getUserProductPrice, setUserProductPrice, deleteUserProductPrice } from '../services/productService';
import type { ProductFull, UserProductPrice, UserPriceType } from '../types/products';
import { PRODUCT_TYPE_LABELS, USER_PRICE_TYPE_LABELS, calculateUserPrice } from '../types/products';
import { logger } from '../lib/logger';

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const [product, setProduct] = useState<ProductFull | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [showTodoToast, setShowTodoToast] = useState(false);

  // User price state
  const [userPrice, setUserPrice] = useState<UserProductPrice | null>(null);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState<UserPriceType>('discount_10');
  const [customPriceInput, setCustomPriceInput] = useState('');
  const [isSavingPrice, setIsSavingPrice] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      if (!slug) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await getProductBySlug(slug);
        if (!data) {
          setError('Produkt nicht gefunden');
        } else {
          setProduct(data);
        }
      } catch (err) {
        logger.error('Failed to load product:', err);
        setError('Fehler beim Laden des Produkts');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [slug]);

  // Load user price
  useEffect(() => {
    const loadUserPrice = async () => {
      if (!user?.id || !product?.id) return;

      try {
        const price = await getUserProductPrice(user.id, product.id);
        setUserPrice(price);
        if (price) {
          setSelectedPriceType(price.price_type);
          if (price.custom_price) {
            setCustomPriceInput(price.custom_price.toString().replace('.', ','));
          }
        }
      } catch (err) {
        logger.error('Failed to load user price:', err);
      }
    };

    loadUserPrice();
  }, [user?.id, product?.id]);

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user) return;
    await createTodo(user.id, input);
    setIsTodoModalOpen(false);
    setShowTodoToast(true);
  };

  const handleSavePrice = async () => {
    if (!user?.id || !product?.id) return;

    setIsSavingPrice(true);
    try {
      const customPrice = selectedPriceType === 'custom'
        ? parseFloat(customPriceInput.replace(',', '.'))
        : null;

      const savedPrice = await setUserProductPrice(user.id, {
        product_id: product.id,
        price_type: selectedPriceType,
        custom_price: customPrice,
      });

      setUserPrice(savedPrice);
      setIsEditingPrice(false);
    } catch (err) {
      logger.error('Failed to save price:', err);
    } finally {
      setIsSavingPrice(false);
    }
  };

  const handleDeletePrice = async () => {
    if (!user?.id || !product?.id) return;

    setIsSavingPrice(true);
    try {
      await deleteUserProductPrice(user.id, product.id);
      setUserPrice(null);
      setIsEditingPrice(false);
      setSelectedPriceType('discount_10');
      setCustomPriceInput('');
    } catch (err) {
      logger.error('Failed to delete price:', err);
    } finally {
      setIsSavingPrice(false);
    }
  };

  const getCalculatedUserPrice = (): number | null => {
    if (!product?.price_gross) return null;
    if (!userPrice) return null;
    return calculateUserPrice(product.price_gross, userPrice.price_type, userPrice.custom_price);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-[#1A1A1A] dark:text-white mb-2">
          {error || 'Produkt nicht gefunden'}
        </h2>
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-primary hover:underline mt-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  // Parse category path into breadcrumb
  const categoryParts = product.category_path?.split(' > ') || [];

  return (
    <div className="space-y-6">
      {showTodoToast && (
        <Toast
          message="Todo erfolgreich erstellt!"
          linkText="Zu den Todos"
          linkTo="/todos"
          onClose={() => setShowTodoToast(false)}
        />
      )}

      {/* Back Link */}
      <Link
        to="/products"
        className="inline-flex items-center gap-2 text-[#6B6B6B] dark:text-slate-400 hover:text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zur Übersicht
      </Link>

      {/* Breadcrumb */}
      {categoryParts.length > 0 && (
        <nav className="flex items-center gap-1 text-sm text-[#6B6B6B] dark:text-slate-400 flex-wrap">
          <Link to="/products" className="hover:text-primary">Produkte</Link>
          {categoryParts.map((part, index) => (
            <span key={index} className="flex items-center gap-1">
              <ChevronRight className="w-4 h-4" />
              <span className={index === categoryParts.length - 1 ? 'text-[#1A1A1A] dark:text-white' : ''}>
                {part}
              </span>
            </span>
          ))}
        </nav>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-[35%_1fr] gap-8">
        {/* Left: Gallery */}
        <div>
          <ProductGallery media={product.media} productName={product.name} />
        </div>

        {/* Right: Product Info */}
        <div className="space-y-6">
          {/* Header */}
          <div>
            {/* Product Type Badge */}
            {product.product_type !== 'single' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary text-white text-xs font-medium rounded-full mb-3">
                <Box className="w-3 h-3" />
                {PRODUCT_TYPE_LABELS[product.product_type]}
              </span>
            )}

            {/* Name with Rating */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white">
                {product.name}
              </h1>
              {product.rating_avg !== null && product.rating_count > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= Math.round(product.rating_avg || 0)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-300 dark:text-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-[#1A1A1A] dark:text-white">
                    {product.rating_avg.toFixed(1)}
                  </span>
                  <span className="text-sm text-[#6B6B6B] dark:text-slate-400">({product.rating_count})</span>
                </div>
              )}
            </div>

            {/* SKU */}
            <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mb-4">Art.-Nr.: {product.sku}</p>

            {/* Prices */}
            <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 space-y-3">
              {/* Online Price */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B6B6B] dark:text-slate-400">Online-Preis:</span>
                <div className="flex items-baseline gap-2">
                  <span className={`text-xl font-bold ${userPrice ? 'text-[#6B6B6B] dark:text-slate-400' : 'text-[#1A1A1A] dark:text-white'}`}>
                    {formatPrice(product.price_gross, product.currency)}
                  </span>
                  {product.base_price_value && product.base_price_unit && (
                    <span className="text-xs text-[#6B6B6B] dark:text-slate-400">
                      ({formatPrice(product.base_price_value, product.currency)} / {product.base_price_unit})
                    </span>
                  )}
                </div>
              </div>

              {/* User Price (if set) */}
              {user && userPrice && !isEditingPrice && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-slate-600">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-primary">Mein Preis:</span>
                    <span className="text-xs text-[#6B6B6B] dark:text-slate-400">({USER_PRICE_TYPE_LABELS[userPrice.price_type]})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-primary">
                      {formatPrice(getCalculatedUserPrice(), product.currency)}
                    </span>
                    <button
                      onClick={() => setIsEditingPrice(true)}
                      className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Set Price Button (if no user price) */}
              {user && !userPrice && !isEditingPrice && (
                <button
                  onClick={() => setIsEditingPrice(true)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors border border-dashed border-primary/30"
                >
                  <Tag className="w-4 h-4" />
                  Eigenen Preis festlegen
                </button>
              )}

              {/* Edit Price Form */}
              {user && isEditingPrice && (
                <div className="pt-3 border-t border-gray-200 dark:border-slate-600 space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-[#1A1A1A] dark:text-white">Eigenen Preis festlegen</span>
                  </div>

                  {/* Price Type Buttons */}
                  <div className="grid grid-cols-4 gap-2">
                    {(['discount_10', 'discount_20', 'discount_30', 'custom'] as UserPriceType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedPriceType(type)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          selectedPriceType === type
                            ? 'bg-primary text-white'
                            : 'bg-white dark:bg-slate-600 border border-gray-200 dark:border-slate-500 text-[#6B6B6B] dark:text-slate-300 hover:border-primary hover:text-primary'
                        }`}
                      >
                        {USER_PRICE_TYPE_LABELS[type]}
                      </button>
                    ))}
                  </div>

                  {/* Custom Price Input */}
                  {selectedPriceType === 'custom' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={customPriceInput}
                        onChange={(e) => setCustomPriceInput(e.target.value)}
                        placeholder="0,00"
                        className="flex-1 px-3 py-2 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-600 text-[#1A1A1A] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      <span className="text-sm text-[#6B6B6B] dark:text-slate-400">{product.currency}</span>
                    </div>
                  )}

                  {/* Preview */}
                  {selectedPriceType !== 'custom' && product.price_gross && (
                    <div className="text-sm text-[#6B6B6B] dark:text-slate-400">
                      Vorschau: <span className="font-semibold text-primary">
                        {formatPrice(calculateUserPrice(product.price_gross, selectedPriceType, null), product.currency)}
                      </span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSavePrice}
                      disabled={isSavingPrice || (selectedPriceType === 'custom' && !customPriceInput)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Check className="w-4 h-4" />
                      Speichern
                    </button>
                    <button
                      onClick={() => setIsEditingPrice(false)}
                      className="px-4 py-2 text-[#6B6B6B] dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {userPrice && (
                      <button
                        onClick={handleDeletePrice}
                        disabled={isSavingPrice}
                        className="px-4 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                        title="Eigenen Preis löschen"
                      >
                        Löschen
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Unit Info */}
            {product.unit_value && product.unit_name && (
              <p className="text-sm text-[#6B6B6B] dark:text-slate-400">
                Inhalt: {product.unit_value} {product.unit_name}
              </p>
            )}
          </div>

          {/* Short Tagline */}
          {product.short_tagline && (
            <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
              <p className="text-[#1A1A1A] dark:text-white font-medium">{product.short_tagline}</p>
            </div>
          )}

          {/* Brand */}
          {product.brand && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#6B6B6B] dark:text-slate-400">Marke:</span>
              <span className="text-sm font-medium text-[#1A1A1A] dark:text-white">{product.brand}</span>
            </div>
          )}

          {/* Todo Button */}
          {user && (
            <button
              onClick={() => setIsTodoModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-xl transition-colors font-medium"
            >
              Todo erstellen
              <CheckSquare className="w-5 h-5" />
            </button>
          )}

          {/* Attributes (Specifications) */}
          <ProductAttributes attributes={product.attributes} />
        </div>
      </div>

      {/* Content Sections */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Sections (Accordion) */}
          <ProductSections sections={product.sections} />

          {/* Bundle Items */}
          {product.product_type !== 'single' && product.bundle_items.length > 0 && (
            <ProductBundleItems items={product.bundle_items} />
          )}
        </div>

        <div className="space-y-6">
          {/* Related Products Card */}
          {(product.relations.some(r => r.relation_type === 'frequently_bought_together') ||
            product.relations.some(r => r.relation_type === 'recommended')) && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 p-4 space-y-6">
              {/* Frequently Bought Together */}
              <ProductRelations relations={product.relations} type="frequently_bought_together" variant="list" />

              {/* Recommended Products */}
              <ProductRelations relations={product.relations} type="recommended" variant="list" />
            </div>
          )}

          {/* Documents */}
          <ProductDocuments documents={product.documents} />

          {/* Marketing Materials */}
          <ProductMarketingMaterials productId={product.id} />
        </div>
      </div>

      {/* Todo Modal */}
      {user && (
        <CreateTodoModal
          isOpen={isTodoModalOpen}
          onClose={() => setIsTodoModalOpen(false)}
          onSubmit={handleCreateTodo}
          defaultTitle={`Produkt: ${product.name}`}
          defaultDescription="Auf Social Media bewerben"
          tag="general"
          sourceType="product"
          sourceId={product.id}
        />
      )}
    </div>
  );
}
